<template>
  <validation-observer v-slot="{ invalid }">
    <b-row>
      <b-col>
        <b-card class="mb-0 mt-0" body-class="p-2">
          <b-table
            :fields="tableFields"
            :items="tableItems"
            :empty-text="'データがありません'"
            hover
            responsive
            show-empty
            class="p-1 text-nowrap table-sm"
            no-local-sorting>
            <!-- 検知エリア設定ファイル -->
            <template v-slot:cell(alertAreaFile)="{ item }">
              <div v-if="item.alertAreaFile && item.alertAreaImageUrl">
                <a
                  @click="clickDownloadLink(item)"
                  href="javascript:void(0)"
                  download>
                  <span>ダウンロード</span>
                </a>
              </div>
              <div v-else>
                <span>未設定</span>
              </div>
            </template>

            <!-- 検知レベル -->
            <template v-slot:cell(alertThreshold)="{ item }">
              {{ alertThresholdsDecode[item.alertThreshold] }}
            </template>

            <!-- 現在の設定状況 -->
            <template v-slot:cell(alertAreaUpdateStatus)="{ item }">
              <h4>
                <b-badge
                  :variant="alertAreaUpdateStatusColor[item.alertAreaUpdateStatus]"
                  style="width:140px;">
                    {{ alertAreaUpdateStatusDecode[item.alertAreaUpdateStatus] }}
                </b-badge>
              </h4>
            </template>

            <!-- 設定ボタン -->
            <template v-slot:cell(configurationButton)="{ item }">
              <b-button
                :id="createId('configurationButton', item)"
                @click="clickConfigurationButton(item.alertAreaId, createId('configurationButton', item))"
                :disabled="isProcessing"
                variant="primary">
                <span>設定</span>
                <b-spinner
                  v-show="buttonstates[createId('configurationButton', item)]"
                  class="ml-1"
                  small>
                </b-spinner>
              </b-button>
            </template>

            <!-- 画像比較ボタン -->
            <template v-slot:cell(imageComparisonButton)="{ item }">
              <b-button
                :id="createId('imageComparisonButton', item)"
                @click="clickImageComparisonButton(item.alertAreaId, createId('imageComparisonButton', item))"
                variant="primary"
                :disabled="isProcessing"
                v-if="item.alertAreaUpdateStatus === 2">
                <span>画像比較</span>
                <b-spinner
                  v-show="buttonstates[createId('imageComparisonButton', item)]"
                  class="ml-1"
                  small>
                </b-spinner>
              </b-button>
            </template>

            <!-- 2021/09/10 検知エリア確認機能追加 -->
            <!-- 検知エリア確認テスト状態変更ボタン -->
            <template v-slot:cell(areaCheckButton)="{ item }">
              <b-button
                class="btn-test"
                v-if="item.trialFlag === 2"
                @click="modal.areaCheckMonitoringStop=true; setAlertAreaId(item.alertAreaId)"
                :variant="selectAreaCheckChangeButtonColor[item.trialFlag]">
                {{ selectAreaCheckName[item.trialFlag] }}
              </b-button>
              <b-button
                class="btn-test"
                v-else
                @click="modal.areaCheckMonitoringStart=true; setAlertAreaId(item.alertAreaId)"
                :variant="selectAreaCheckChangeButtonColor[item.trialFlag]">
                {{ selectAreaCheckName[item.trialFlag] }}
              </b-button>
            </template>
          </b-table>
        </b-card>
      </b-col>

      <!-- 検知条件設定モーダル -->
      <b-modal
        v-model="modal.configuration"
        title="検知条件設定"
        size="xl"
        no-close-on-backdrop>
        <b-container fluid>
          <!-- 検知条件設定モーダル.踏切名 -->
          <b-row>
            <b-col cols="2" align-self="center">
              踏切名
            </b-col>
            <b-col cols="8" align-self="center">
              <span v-text="modalItems.crossingName"></span>
            </b-col>
          </b-row>
          <br />
          <!-- 検知条件設定モーダル.カメラ設置場所 -->
          <b-row>
            <b-col cols="2" align-self="center">
              カメラ設置場所
            </b-col>
            <b-col cols="8" align-self="center">
              <span v-text="modalItems.locationName"></span>
            </b-col>
          </b-row>
          <br />
          <!-- 検知条件設定モーダル.最新画像のサムネイル -->
          <div v-if="modalItems.alertAreaImageUrl">
            <b-row>
              <b-col cols="2" align-self="end"></b-col>
              <b-col cols="3" align-self="center">
                <div class="pull-left">
                  <b-img-lazy
                    class="modal-image"
                    v-bind="imageProperties"
                    :src="modalItems.alertAreaImageUrl"
                    alt="最新画像" >
                  </b-img-lazy>
                </div>
              </b-col>
            </b-row>
            <br />
            <!-- 検知条件設定モーダル.現在設定されている画像リンク -->
            <b-row align-h="end">
              <b-col cols="4" align-self="center">
                <a
                  :href="modalItems.alertAreaImageUrl"
                  :download="modalItems.alertAreaImageFileName"
                  target="_blank">
                  現在設定されている画像
                </a>
              </b-col>
            </b-row>
          </div>
          <!-- 検知条件設定モーダル.検知エリア画像ファイル選択 -->
          <b-row>
            <b-col cols="2" align-self="center">検知エリア設定画像</b-col>
            <b-col cols="8" align-self="center">
              <b-form-file
                v-model="modalItems.alertAreaImageFile"
                accept=".jpg"
                :placeholder="setAlertAreaImageFilePlaceholder()"
                browse-text="選択"
                :disabled="isProcessing">
              </b-form-file>
            </b-col>
          </b-row>
          <!-- 検知条件設定モーダル.現在設定されているファイルリンク -->
          <div v-if="modalItems.alertAreaFile">
            <b-row align-h="end">
              <b-col cols="4" align-self="center">
                <a
                  :href="modalItems.alertAreaFile"
                  :download="modalItems.alertAreaNpyFileName"
                  target="_blank">
                  現在設定されているファイル
                </a>
              </b-col>
            </b-row>
          </div>
          <div v-else>
            <br />
          </div>
          <!-- 検知条件設定モーダル.検知エリア設定ファイル選択 -->
          <b-row>
            <b-col cols="2" align-self="center">検知エリア設定ファイル</b-col>
            <b-col cols="8" align-self="center">
              <b-form-file
                v-model="modalItems.alertAreaNpyFile"
                accept=".npy"
                :placeholder="setAlertAreaNpyFilePlaceholder()"
                browse-text="選択"
                :disabled="isProcessing">
              </b-form-file>
            </b-col>
          </b-row>
          <br />
          <!-- 検知条件設定モーダル.検知レベル -->
          <b-row>
            <b-col cols="2" align-self="center">検知レベル</b-col>
            <b-col cols="8" align-self="center">
              <validation-provider
                v-slot="validationContext"
                rules="required"
                name="検知レベル">
                <b-form-select
                  v-model="modalItems.alertThreshold"
                  :options="modalItems.alertThresholdOptions"
                  :state="getValidationState(validationContext)"
                  :disabled="isProcessing">
                </b-form-select>
                <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
              </validation-provider>
            </b-col>
          </b-row>
          <br />
          <!-- 検知条件設定モーダル.現在の設定状況 -->
          <b-row>
            <b-col cols="2" align-self="center">現在の設定状況</b-col>
            <b-col cols="8" align-self="center">
              <h4>
                <b-badge
                  :variant="alertAreaUpdateStatusColor[modalItems.alertAreaUpdateStatus]"
                  style="width:140px;">
                    {{ alertAreaUpdateStatusDecode[modalItems.alertAreaUpdateStatus] }}
                </b-badge>
              </h4>
            </b-col>
          </b-row>
        </b-container>

        <template v-slot:modal-footer="{ cancel }">
          <!-- 検知条件設定モーダル.最新画像取得 -->
          <b-button
            @click="clickConfigurationModalLatestImageButton()"
            variant="primary"
            style="margin-right:100px;"
            :disabled="isProcessing"
            >
            <span>最新画像取得</span>
            <b-spinner
              v-show="buttonstates.latestImage"
              class="ml-1"
              small>
            </b-spinner>
          </b-button>
          <!-- 検知条件設定モーダル.キャンセル -->
          <b-button
            variant="secondary"
            @click="cancel()"
            :disabled="isProcessing"
            >
            <span>キャンセル</span>
          </b-button>
          <!-- 検知条件設定モーダル.登録 -->
          <b-button
            variant="primary"
            @click="registrationBtn()"
            :disabled="invalid || isProcessing">
            <span>登録</span>
            <b-spinner
              v-show="buttonstates.registButton"
              class="ml-1"
              small>
            </b-spinner>
          </b-button>
        </template>
      </b-modal>

      <!-- 画像比較モーダル -->
      <b-modal
        v-model="modal.imageComparison"
        title="検知条件設定"
        size="xl"
        no-close-on-backdrop
        footer-class="pb-2 border-top-0">
        <b-container fluid>
          <!-- 画像比較モーダル.踏切名 -->
          <b-row>
            <b-col cols="2" align-self="center">踏切名</b-col>
            <b-col cols="8" align-self="center">
              <span v-text="modalItems.crossingName"></span>
            </b-col>
          </b-row>
          <br />
          <!-- 画像比較モーダル.カメラ設置場所 -->
          <b-row>
            <b-col cols="2" align-self="center">カメラ設置場所</b-col>
            <b-col cols="8" align-self="center">
              <span v-text="modalItems.locationName"></span>
            </b-col>
          </b-row>
          <br />
          <b-row>
            <!-- 画像比較モーダル.設定時の画像 -->
            <b-col class="cols-12">
              <span class="ml-auto align-self-end">
                設定時の画像
              </span>
            </b-col>
            <!-- 画像比較モーダル.現在の画像 -->
            <b-col class="cols-12">
              <span class="ml-auto align-self-end">
                現在の画像
              </span>
              <div class="pull-right">
                <!-- 画像比較モーダル.最新画像取得ボタン -->
                <b-button
                  @click="clickImageComparisonModalLatestImageButton()"
                  :disabled="isProcessing"
                  variant="primary">
                  <span>最新画像取得</span>
                  <b-spinner
                    v-show="buttonstates.latestImage"
                    class="ml-1"
                    small>
                  </b-spinner>
                </b-button>
              </div>
            </b-col>
          </b-row>
          <br />
          <b-row>
            <!-- 画像比較モーダル.設定時の画像 -->
            <b-col class="cols-12">
              <b-img-lazy
                class="modal-image2"
                v-bind="imageProperties"
                :src="modalItems.alertAreaImageUrl"
                alt="設定時の画像" >
              </b-img-lazy>
            </b-col>
            <!-- 画像比較モーダル.最新画像 -->
            <b-col class="cols-12">
              <b-img-lazy
                class="modal-image2"
                v-bind="imageProperties"
                :src="modalItems.latestImage"
                alt="最新画像" >
              </b-img-lazy>
            </b-col>
          </b-row>
        </b-container>
        <!-- 画像比較モーダル.閉じるボタン -->
        <template v-slot:modal-footer>
          <b-button
            variant="primary"
            size="md"
            :disabled="isProcessing"
            @click="modal.imageComparison = false">
            閉じる
          </b-button>
        </template>
      </b-modal>

      <!-- 進行度モーダル -->
      <b-modal
        v-model="modal.progress"
        size="md"
        hide-header
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
        >
        <b-container fluid>
          <!-- 進行度 -->
          <b-row>
            <b-col cols="8" offset="2" class="text-center" align-self="center">
              <span v-text="progressDecode[progressValue]"></span>
            </b-col>
          </b-row>
          <br />
          <b-row>
            <b-col cols="8" offset="2" align-self="center">
              <!-- プログレスバー(枠線) -->
              <div
                class="bg-secondary border border-secondary rounded"
                :style="progressBarFlameStyle">
                <!-- プログレスバー -->
                <div
                  :class="progressBarClass[progressValue === 2]"
                  :style="progressBarStyle">
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="text-center" align-self="center">
              <span v-text="progressValue + ' / 2'"></span>
            </b-col>
          </b-row>
        </b-container>
      </b-modal>

      <!-- 2021/09/10 検知エリア確認機能追加 -->
      <!-- 開始確認モーダル -->
      <b-modal
        v-model="modal.areaCheckMonitoringStart"
        title="検知エリア確認テスト開始確認"
        header-bg-variant="warning"
        hide-header-close
        no-close-on-esc
        no-close-on-backdrop>
        <span>検知エリア確認テストを開始しますが、よろしいでしょうか？<br /><br />
        検知エリア確認テストが開始されると試運用モードに切り替わります。<br />
        テスト終了後に試運用モードを解除してください。</span>
        <template v-slot:modal-footer="{ cancel }">
          <b-button
            variant="secondary"
            @click="cancel()">
            <span>Cansel</span>
          </b-button>
          <b-button
            variant="primary"
            @click="areaCheckMonitoringStart()">
            <span>OK</span>
            <b-spinner
              v-show="isProcessing"
              class="ml-1"
              small>
            </b-spinner>
          </b-button>
        </template>
      </b-modal>

      <!-- 2021/09/10 検知エリア確認機能追加 -->
      <!-- 停止確認モーダル -->
      <b-modal
        v-model="modal.areaCheckMonitoringStop"
        title="検知エリア確認テスト終了確認"
        header-bg-variant="warning"
        hide-header-close
        no-close-on-esc
        no-close-on-backdrop>
        <span>検知エリア確認テストを終了しますが、よろしいでしょうか？<br /><br />
        試運用モードに切り替わっていますので、テスト終了後に試運用モードを解除してください。</span>
        <template v-slot:modal-footer="{ cancel }">
          <b-button
            variant="secondary"
            @click="cancel()">
            <span>Cansel</span>
          </b-button>
          <b-button
            variant="primary"
            @click="areaCheckMonitoringStop()">
            <span>OK</span>
            <b-spinner
              v-show="isProcessing"
              class="ml-1"
              small>
            </b-spinner>
          </b-button>
        </template>
      </b-modal>

    </b-row>
  </validation-observer>
</template>

<script>
  import API from "@/api/index"
  import {mapGetters, mapMutations} from "vuex"
  import {operations} from '@/shared/operations'

  // 2021/09/10 検知エリア確認機能追加
  const MONITORING_START = 2
  const MONITORING_STOP = 1

  export default {
    name: "DetectionArea",
    data() {
      return {
        // ダイアログを多重に表示させない為のフラグ
        alreadyDialogDisplayed: false,
        // エラーダイアログの表示状態
        alreadyErrorDialogDisplayed: false,

        // 検知条件設定一覧
        tableItems: [],
        // モーダル
        modal: {
          configuration: false,
          imageComparison: false,
          progress: false,
          areaCheckMonitoringStart: false, // 2021/09/10 検知エリア確認機能追加
          areaCheckMonitoringStop: false, // 2021/09/10 検知エリア確認機能追加
        },
        modalItems: {},
        // ボタン状態
        buttonstates: {},

        // 画像のプロパティ
        imageProperties: {
          center: true,
          width: 800,
          height: 600,
          blankColor: "#99999999",
          fluid: true,
        },

        // 進行状況
        progressValue: 0,

        // 2021/09/10 検知エリア確認機能追加
        // 選択行の検知エリアID
        selectedAlertAreaId: null,
      }
    },

    computed: {
      ...mapGetters("auth", ['idToken', 'clientId', 'affiliationId']),
      ...mapGetters("componentState", ['isProcessing']),

      // エラーダイアログのプロパティ
      errorDialogProps() {
        return {
          title: 'エラー',
          headerBgVariant: 'danger',
          hideHeaderClose: true,
          noCloseOnEsc: true,
          noCloseOnBackdrop: true,
        }
      },

      // 成功ダイアログのプロパティ
      successDialogProps() {
        return {
          title: '成功',
          headerBgVariant: 'success',
          hideHeaderClose: true,
          noCloseOnEsc: true,
          noCloseOnBackdrop: true
        }
      },

      // 検知条件設定.テーブルカラム
      tableFields() {
        return [
          {key: 'crossingName', label: '踏切名', sortable: false},
          {key: 'locationName', label: 'カメラ設置場所', sortable: false},
          {key: 'alertAreaFile', label: '検知エリア設定ファイル', sortable: false},
          {key: 'alertThreshold', label: '検知レベル', sortable: false},
          {key: 'alertAreaUpdateStatus', label: '現在の設定状況', sortable: false},
          {key: 'configurationButton', label: '', sortable: false},
          {key: 'imageComparisonButton', label: '', sortable: false},
          {key: 'areaCheckButton', label: '', sortable: false}, // 2021/09/10 検知エリア確認機能追加
        ]
      },

      // 検知レベルのデコード
      alertThresholdsDecode() {
        return {
          0: '低',
          1: '中',
          2: '高',
        }
      },

      // 現在の設定状況のデコード
      alertAreaUpdateStatusDecode() {
        return {
          0: '未設定',
          1: '設定失敗',
          2: '設定完了',
        }
      },

      // 現在の設定状況の色
      alertAreaUpdateStatusColor() {
        return {
          0: 'secondary',
          1: 'danger',
          2: 'success',
        }
      },

      // 進行度
      progressDecode() {
        return {
          0: '検知エリア情報を登録中',
          1: '検知エリア情報をエッジデバイスに反映中',
          2: '検知エリア情報の反映が完了しました',
        }
      },

      // 2021/09/10 検知エリア確認機能追加
      // テスト開始/終了ボタン
      selectAreaCheckName() {
        return {
          0: 'テスト開始',
          1: 'テスト開始',
          2: 'テスト終了',
        }
      },
      selectAreaCheckChangeButtonColor() {
        return {
          0: 'success',
          1: 'success',
          2: ''
        }
      },

      // プログレスバー(枠線)のスタイル
      progressBarFlameStyle() {
        return {
          position: 'relative',
          width: '100%',
          height: '14px',
        }
      },

      // プログレスバーのクラス
      progressBarClass() {
        const baseClass = 'bg-success border border-secondary rounded-left'
        return {
          true: baseClass + ' rounded-right',
          false: baseClass
        }
      },

      // プログレスバーのスタイル
      progressBarStyle() {
        const maxWidth = 100
        const width = maxWidth / 2 * this.progressValue
        return {
          position: 'absolute',
          width: width + '%',
          height: '10px',
          margin: '1px 0px 0px',
        }
      },

      // 更新用パラメータ
      async updateParams() {
        const item = this.modalItems
        var params = {}
        params.clientId = Number(this.clientId)
        params.affiliationId = Number(this.affiliationId)
        params.alertAreaId = Number(item.alertAreaId)
        params.alertAreaImage = await this.readFileAsDataURL(item.alertAreaImageFile)
        params.alertAreaImageFileName = item.alertAreaImageFile.name
        params.alertAreaFile = await this.readFileAsDataURL(item.alertAreaNpyFile)
        params.alertAreaFileName = item.alertAreaNpyFile.name
        params.alertThreshold = Number(item.alertThreshold)
        params.updateDate = item.updateDate
        return params
      },

      // 反映用パラメータ
      reflectParams() {
        const item = this.modalItems
        return {
          alertAreaId: Number(item.alertAreaId),
        }
      },
    },
    // 画面の初期表示時
    async created() {
      await this.initialize()
    },

    // 画面の終了時
    beforeDestroy() {},

    methods: {
      ...mapMutations("componentState", [
        'turnLoadingOn',
        'turnLoadingOff',
      ]),

      // バリデーション
      getValidationState: ({dirty, validated, valid = null}) => dirty || validated ? valid : null,

      // ID作成
      createId(fixId, item) {
        return fixId + item.__ob__.dep.id
      },

      // 初期化処理
      async initialize() {
        // 検知エリア情報の取得(複数件)
        const detectionArea = await this.getDetectionArea(null)
        this.tableItems = detectionArea
      },

      // 検知エリア情報の取得
      async getDetectionArea(alertAreaId) {
        try {
          var detectionArea = null
          // 検知エリア情報の取得
          var queryParamArray = []
          queryParamArray.push('?client_id=')
          queryParamArray.push(this.clientId)
          queryParamArray.push('&affiliation_id=')
          queryParamArray.push(this.affiliationId)
          if (alertAreaId) {
            queryParamArray.push('&alert_area_id=')
            queryParamArray.push(alertAreaId)
          }
          const queryParam = queryParamArray.join('')
          detectionArea = await API.detectionArea.get(this.idToken(), {path: queryParam})

          detectionArea.data.forEach(function(data) {
            // 検知エリア画像ファイル
            data.alertAreaImageFile = null
            // 検知エリア画像ファイル名
            const imageUrl = data.alertAreaImageUrl
            data.alertAreaImageFileName = imageUrl ? imageUrl.split('/').pop() : null
            // 検知エリア設定ファイル
            data.alertAreaNpyFile = null
            // 検知エリア設定ファイル名
            const npyUrl = data.alertAreaFile
            data.alertAreaNpyFileName = npyUrl ? npyUrl.split('/').pop() : null
          })
          return detectionArea.data
        } catch (error) {
          // レスポンスを持っているか
          if (!(error && error.hasOwnProperty('response'))) return
          // ステータスコードを持っているか
          if (!(error.response && error.response.hasOwnProperty('status'))) return
          if (error.response.status === 403)
            return this.$router.push({ path: '/session-timeout' })
          await this.showErrorDialog('検知エリア情報の取得に失敗しました。')
        }
      },

      // 検知レベルのプルダウンを作成
      async createAlertThresholdOption() {
        var alertThresholdOptions = []
        alertThresholdOptions.push({ value: 0, text: '低' })
        alertThresholdOptions.push({ value: 1, text: '中' })
        alertThresholdOptions.push({ value: 2, text: '高' })
        return alertThresholdOptions
      },

      // ダウンロードリンク押下時
      async clickDownloadLink(item) {
        let errorMes = ''
        const alertAreaFileUrl = await this.getImageFile(item.alertAreaFile)
        if (alertAreaFileUrl != null) {
          await this.downloadFile(alertAreaFileUrl, item.alertAreaNpyFileName)
        } else {
          errorMes = errorMes + '検知エリア設定ファイル取得に失敗しました\n'
        }
        const alertAreaImageUrl = await this.getImageFile(item.alertAreaImageUrl)
        if (alertAreaImageUrl != null) {
          // 検知エリア画像ファイル
          await this.downloadFile(alertAreaImageUrl, item.alertAreaImageFileName)
        } else {
          errorMes = errorMes + '設定時の画像取得に失敗しました\n'
        }
        if (errorMes != '') {
          await this.showErrorDialog(errorMes)
        }
      },

      async downloadFile(fileUrl, fileName) {
        // 一時的に<a>タグを作成し、クリックする
        const tempLink = document.createElement('a')
        document.body.appendChild(tempLink)
        tempLink.download = fileName
        tempLink.href = fileUrl
        tempLink.target = '_blank'
        tempLink.click()
        tempLink.remove()
      },

      // 検知条件設定モーダルの初期化
      initModal() {
        this.modalItems = {}
      },

      // 設定ボタン押下時
      async clickConfigurationButton(alertAreaId, buttonId) {
        this.turnLoadingOn()
        // 押下ボタンのスピナーを表示
        this.buttonstates[buttonId] = true
        // 編集状態の変更
        this.editting = operations.update.en

        // 検知エリア情報の取得(単一件)
        const detectionArea = await this.getDetectionArea(alertAreaId)
        if (detectionArea == null) {
          // 押下ボタンのスピナーを非表示
          this.buttonstates[buttonId] = false
          this.turnLoadingOff()
          return
        }
        // 取得情報なし
        if (!detectionArea.length) {
          await this.$bvModal.msgBoxOk(`他者によって情報が削除されています`, this.errorDialogProps)
          .then(async () => { await this.initialize() })
          .catch(() => {})
        // 取得情報あり
        } else {
          // 検知条件設定モーダルの初期化
          this.initModal()

          const data = detectionArea[0]

          // 検知条件設定モーダル.検知エリアID
          this.modalItems.alertAreaId = data.alertAreaId
          // 検知条件設定モーダル.カメラID
          this.modalItems.cameraId = data.cameraId
          // 検知条件設定モーダル.踏切名
          this.modalItems.crossingName = data.crossingName
          // 検知条件設定モーダル.カメラ設置場所
          this.modalItems.locationName = data.locationName

          let errorMes = ''
          if (data.alertAreaImageUrl != null) {
            const imageUrl = await this.getImageFile(data.alertAreaImageUrl)
            if (imageUrl != null) {
              this.modalItems.alertAreaImageUrl = imageUrl
            } else {
              errorMes = errorMes + '設定時の画像取得に失敗しました\n'
            }
          } else {
            this.modalItems.alertAreaImageUrl = data.alertAreaImageUrl
          }
          // 検知条件設定モーダル.最新画像のサムネイル 兼
          // 検知条件設定モーダル.現在設定されている画像
          // 検知条件設定モーダル.現在設定されている検知エリア画像ファイル名
          this.modalItems.alertAreaImageFileName = data.alertAreaImageFileName
          // 検知条件設定モーダル.検知エリア設定画像
          this.modalItems.alertAreaImageFile = data.alertAreaImageFile

          // 検知条件設定モーダル.現在設定されているファイル
          if (data.alertAreaFile != null) {
            const imageUrl = await this.getImageFile(data.alertAreaFile)
            if (imageUrl != null) {
              this.modalItems.alertAreaFile = imageUrl
            } else {
              errorMes = errorMes + '検知エリア設定ファイル取得に失敗しました\n'
            }
          } else {
            this.modalItems.alertAreaFile = data.alertAreaFile
          }

          // 検知条件設定モーダル.現在設定されている検知エリア設定ファイル名
          this.modalItems.alertAreaNpyFileName = data.alertAreaNpyFileName
          // 検知条件設定モーダル.検知エリア設定ファイル
          this.modalItems.alertAreaNpyFile = data.alertAreaNpyFile

          // 検知条件設定モーダル.検知レベル
          this.modalItems.alertThresholdOptions = await this.createAlertThresholdOption()
          this.modalItems.alertThreshold = data.alertThreshold
          // 検知条件設定モーダル.現在の設定状況
          this.modalItems.alertAreaUpdateStatus = data.alertAreaUpdateStatus
          // 検知条件設定モーダル.更新日時
          this.modalItems.updateDate = data.updateDate

          if (errorMes != '') {
            await this.showErrorDialog(errorMes)
          }

          // モーダル表示
          this.modal.configuration = true
        }
        // 押下ボタンのスピナーを非表示
        this.buttonstates[buttonId] = false
        this.turnLoadingOff()
      },

      // 設定モーダル.検知エリア設定画像のプレースホルダ
      setAlertAreaImageFilePlaceholder() {
        if (this.modalItems.alertAreaImageFileName) {
          return '前回設定ファイル：' + this.modalItems.alertAreaImageFileName
        } else {
          return '画像を選択してください'
        }
      },

      // 設定モーダル.検知エリア設定ファイルのプレースホルダ
      setAlertAreaNpyFilePlaceholder() {
        if (this.modalItems.alertAreaNpyFileName) {
          return '前回設定ファイル：' + this.modalItems.alertAreaNpyFileName
        } else {
          return 'ファイルを選択してください'
        }
      },

      // 検知条件設定モーダル.ヘッダー.登録ボタン押下時
      async registrationBtn() {
        this.buttonstates.registButton = true
        // 押下ボタンのスピナーを表示
        this.turnLoadingOn()
        // 単項目チェック
        var errMsg = await this.single()
        if (errMsg) {
          // 押下ボタンのスピナーを非表示
          this.buttonstates.registButton = false
          this.turnLoadingOff()
          return this.$bvModal.msgBoxOk(errMsg, this.errorDialogProps)
        }
        // 相関チェック
        errMsg = await this.correlation()
        if (errMsg) {
          // 押下ボタンのスピナーを非表示
          this.buttonstates.registButton = false
          this.turnLoadingOff()
          return this.$bvModal.msgBoxOk(errMsg, this.errorDialogProps)
        }
        // 更新
        await this.operateData(this.editting)
        // 押下ボタンのスピナーを非表示
        this.buttonstates.registButton = false
        this.turnLoadingOff()
      },

      // 単項目チェック
      async single() {
        var errMsg = ''
        const item = this.modalItems

        // ■ 検知エリア設定画像
        // 前回設定画像なし かつ 今回設定画像なしの場合
        if (!item.alertAreaImageUrl && !item.alertAreaImageFile) {
          errMsg = '初回は検知エリア設定画像が必須入力です'
        }
        if (errMsg) return errMsg

        // ■ 検知エリア設定ファイル
        // 前回設定ファイルなし かつ 今回設定ファイルなしの場合
        if (!item.alertAreaFile && !item.alertAreaNpyFile) {
          errMsg = '初回は検知エリア設定ファイルが必須入力です'
        }
        if (errMsg) return errMsg
        return null
      },

      // 相関チェック
      async correlation() {
        var errMsg = ''
        const item = this.modalItems
        // 検知エリア設定画像 と 検知エリア設定画像 の片方だけ入力されている場合
        if ((item.alertAreaImageFile && !item.alertAreaNpyFile)
            || (!item.alertAreaImageFile && item.alertAreaNpyFile)) {
          errMsg = '検知エリア設定画像と検知エリア設定ファイルの整合性が取れていません'
        }
        if (errMsg) return errMsg
        return null
      },

      // 登録・更新・削除処理
      async operateData(operation) {
        try {
          // 進行度モーダルの表示
          this.progressValue = 0
          this.modal.progress = true
          // 検知エリア情報のDB更新
          await this.operateAPI(operation)
          this.progressValue = 1
          // 検知エリア情報の反映
          await API.reflectDetectionArea.post(
              this.idToken(), {data: this.reflectParams})
          this.progressValue = 2
          var operationName = null
          switch (operation) {
            case operations.register.en:
            case operations.update.en:
              operationName = this.getOperationName(operations.register.en)
              break
            case operations.delete.en:
              operationName = this.getOperationName(operations.delete.en)
              break
          }
          this.$bvModal.msgBoxOk(
              `${operationName}が完了しました。`, this.successDialogProps)
            .then(async() => {
                switch (operation) {
                  case operations.register.en:
                  case operations.update.en:
                    this.modal.registration = false
                    break
                  case operations.delete.en:
                }
                this.modal.progress = false
                this.modal.configuration = false
                await this.initialize()
              })
            .catch(() => {}
          )
        } catch (error) {
          // レスポンスを持っているか
          if (!(error && error.hasOwnProperty('response'))) return
          // ステータスコードを持っているか
          if (!(error.response && error.response.hasOwnProperty('status'))) return
          if (error.response.status === 403) return this.$router.push({path: '/session-timeout'})
          // エラーメッセージ設定
          await this.showErrorDialog(this.createErrorMessage(error))
          if (error.response.status === 409) {
            this.modal.progress = false
            this.modal.configuration = false
            await this.initialize()
          }
          return
        }
      },

      // APIの呼び出し
      async operateAPI(operation) {
        switch (operation) {
          // 登録
          case operations.register.en:
            return
          // 更新
          case operations.update.en:
            const config = {
              headers: {'content-type': 'application/json'}
            }
            return API.detectionArea.patch(
                this.idToken(), { data: await this.updateParams, action: config})
          // 削除
          case operations.delete.en:
            return
        }
      },

      // パラメータを日本語表記に変換
      getOperationName(operation) {
        switch (operation) {
          case operations.register.en:
            return operations.register.ja
          case operations.update.en:
            return operations.update.ja
          case operations.delete.en:
            return operations.delete.ja
        }
      },

      // 検知条件設定モーダル.最新画像取得ボタン押下時
      async clickConfigurationModalLatestImageButton() {
        this.turnLoadingOn()
        this.buttonstates.latestImage = true
        // 最新画像の取得
        var queryParamArray = []
        queryParamArray.push('?camera_id=')
        queryParamArray.push(this.modalItems.cameraId)
        const queryParam = queryParamArray.join('')

        // 画像取得
        const latestImageUrl = await this.getlatestImage(queryParam)
        if (latestImageUrl != null) {
          // ダウンロード
          await this.downloadFile(latestImageUrl, 'latestImage.jpg')
        } else {
          await this.showErrorDialog('最新画像の取得に失敗しました。')
        }
        this.buttonstates.latestImage = false
        this.turnLoadingOff()
      },

      // 画像比較ボタン押下時
      async clickImageComparisonButton(alertAreaId, buttonId) {
        this.turnLoadingOn()
        // 押下ボタンのスピナーを表示
        this.buttonstates[buttonId] = true

        // 検知エリア情報の取得(単一件)
        const detectionArea = await this.getDetectionArea(alertAreaId)
        if (detectionArea == null) {
          // 押下ボタンのスピナーを非表示
          this.buttonstates[buttonId] = false
          this.turnLoadingOff()
          return
        }
        // 取得情報なし
        if (!detectionArea.length) {
          await this.$bvModal.msgBoxOk(`他者によって情報が削除されています`, this.errorDialogProps)
          .then(async () => { await this.initialize() })
          .catch(() => {})
        // 取得情報あり
        } else {
          // 画像比較モーダルの初期化
          this.initModal()
          let errorMes = ''
          const data = detectionArea[0]

          // 画像比較モーダル.カメラID
          this.modalItems.cameraId = data.cameraId
          // 画像比較モーダル.踏切名
          this.modalItems.crossingName = data.crossingName
          // 画像比較モーダル.カメラ設置場所
          this.modalItems.locationName = data.locationName
          // 画像比較モーダル.検知エリア設定画像
          if (data.alertAreaImageUrl != null) {
            const imageUrl = await this.getImageFile(data.alertAreaImageUrl)
            if (imageUrl != null) {
              this.modalItems.alertAreaImageUrl = imageUrl
            } else {
              errorMes = errorMes + '検知エリア設定画像取得に失敗しました\n'
            }
          } else {
            this.modalItems.alertAreaImageUrl = data.alertAreaImageUrl
          }

          // 画像比較モーダル.最新画像
          var queryParamArray = []
          queryParamArray.push('?camera_id=')
          queryParamArray.push(this.modalItems.cameraId)
          const queryParam = queryParamArray.join('')
          const latestImageUrl = await this.getlatestImage(queryParam)
          if (latestImageUrl != null) {
            this.modalItems.latestImage = latestImageUrl
          } else {
            errorMes = errorMes + '最新画像の取得に失敗しました\n'
          }

          // 画像比較モーダル.更新日時
          this.modalItems.updateDate = data.updateDate

          if (errorMes != '') {
            await this.showErrorDialog(errorMes)
          }

          // モーダル表示
          this.modal.imageComparison = true
        }
        // 押下ボタンのスピナーを非表示
        this.buttonstates[buttonId] = false
        this.turnLoadingOff()
      },

      // 画像比較モーダル.最新画像取得ボタン押下時
      async clickImageComparisonModalLatestImageButton() {
        // 最新画像の取得
        this.turnLoadingOn()
        this.buttonstates.latestImage = true

        // 最新画像の取得
        var queryParamArray = []
        queryParamArray.push('?camera_id=')
        queryParamArray.push(this.modalItems.cameraId)
        const queryParam = queryParamArray.join('')

        // 画像取得
        const latestImageUrl = await this.getlatestImage(queryParam)
        if (latestImageUrl != null) {
          this.$set(this.modalItems, 'latestImage', latestImageUrl)
          // 画像比較モーダル.カメラ設置場所を更新
          var updateModalItems = this.modalItems
          updateModalItems.latestImage = latestImageUrl
          this.modalItems = Object.assign({}, this.modalItems, updateModalItems)
        } else {
          await this.showErrorDialog('最新画像の取得に失敗しました。')
        }

        this.buttonstates.latestImage = false
        this.turnLoadingOff()
      },

      // 2021/09/10 検知エリア確認機能追加
      // 選択行の検知エリアIDを保持
      setAlertAreaId(alertAreaId) {
        this.selectedAlertAreaId = alertAreaId
      },

      // 2021/09/10 検知エリア確認機能追加
      // テスト開始ボタン押下時
      async areaCheckMonitoringStart() {
        this.turnLoadingOn();
        try {
          var params = {}
          params.alertAreaId = Number(this.selectedAlertAreaId)
          params.processType = Number(MONITORING_START)

          await API.areaCheckMonitoring.post(
            this.idToken(), {data: params})
          this.$bvModal.msgBoxOk(
            `検知エリア確認テストを開始しました。`, this.successDialogProps)
        } catch (err) {
          // セッションタイムアウト判定
          if (err.response && err.response.status === 403) {
            return this.$router.push({path: '/session-timeout'})
          } else {
            await this.showErrorDialog(`検知エリア確認テストの開始要求に失敗しました。(${err.message})`)
          }
        }

        // 一覧情報を更新
        await this.initialize()

        this.turnLoadingOff();
        this.modal.areaCheckMonitoringStart = false
      },

      // テスト終了ボタン押下時
      async areaCheckMonitoringStop() {
        this.turnLoadingOn();
        try {
          var params = {}
          params.alertAreaId = Number(this.selectedAlertAreaId)
          params.processType = Number(MONITORING_STOP)

          await API.areaCheckMonitoring.post(
            this.idToken(), {data: params})
          this.$bvModal.msgBoxOk(
            `検知エリア確認テストを終了しました。`, this.successDialogProps)
        } catch (err) {
          // セッションタイムアウト判定
          if (err.response && err.response.status === 403) {
            return this.$router.push({path: '/session-timeout'})
          } else {
            await this.showErrorDialog(`検知エリア確認テストの終了要求に失敗しました。(${err.message})`)
          }
        }

        // 一覧情報を更新
        await this.initialize()

        this.turnLoadingOff();
        this.modal.areaCheckMonitoringStop = false
      },

      // エラーメッセージの作成
      createErrorMessage(error) {
        let result = error.response.data
        return result
      },

      // エラーダイアログの表示
      async showErrorDialog(errorMessage) {
        this.alreadyDialogDisplayed = true
        // 既にエラーダイアログが表示されている場合は重ねて表示しない
        if (this.alreadyErrorDialogDisplayed === true) return
        this.alreadyErrorDialogDisplayed = true
        await this.$bvModal.msgBoxOk(
          `${errorMessage}`, this.errorDialogProps)
          .then(async () => {
            this.modal.progress = false
          })
          .catch(() => {
            this.alreadyDialogDisplayed = false
            this.alreadyErrorDialogDisplayed = false
          })
        this.alreadyDialogDisplayed = false
        this.alreadyErrorDialogDisplayed = false
      },

      // 画像ファイル取得(return URL)
      async getImageFile(imageUrl) {
        try {
          const soundUrl = '?filePath=' + encodeURIComponent(imageUrl)
          console.log('-------- soundUrl --------')
          console.log(soundUrl)
          const downLoadFileResult = await API.fileDownLoad.downLoadFile(
            this.idToken(),
            {
              'Content-Type': 'image/jpeg',
              path: soundUrl
            }
          )
          const blob = new Blob([downLoadFileResult], {type: "image/jpeg"})
          return window.URL.createObjectURL(blob)
        } catch (error) {
          return null
        }
      },

      // 最新画像ファイル取得
      async getlatestImage(queryParam) {
        try {
          const latestImage = await API.latestImage.downLoadFile(
            this.idToken(),
            {
              path: queryParam
            }
          )

          const blob = new Blob([latestImage], {type: "image/jpeg"})
          return window.URL.createObjectURL(blob)
        } catch (error) {
          return null
        }
      },

      // ファイル読み込み
      async readFileAsDataURL(file) {
        let result_base64 = await new Promise((resolve) => {
          let fileReader = new FileReader();
          fileReader.onload = (e) => resolve(fileReader.result.replace(/data:.*\/.*;base64,/, ''));
          fileReader.readAsDataURL(file);
        })
        return result_base64
      },

    },
  }
</script>

<style lang="scss" scoped>
  .modal-image {
    width: auto;
    height: auto;
    max-height: calc(100vh - 545px);
  }

  .modal-image2 {
    width: auto;
    height: auto;
    max-height: calc(100vh - 180px);
  }

  .px-12p {
    padding-left: 12px;
    padding-right: 12px;
  }

  .table_vertical {
    vertical-align: middle
  }

  .btn-test {
      border: solid 1px #000000;
      width:120px;
      color: #000000;
  }

  .badge-secondary {
      color: #ffffff;
      background-color: #949a9e;
  }

  input[type=time]::-webkit-inner-spin-button,
  input[type=time]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
</style>
